/**
 * Splitting a typed full name into the parts the API stores.
 *
 * The forms ask for one "Full name" field because that is how people think of
 * a name and how it arrives in a spreadsheet, but investigations are stored
 * with first/middle/last separately. This is the one place that conversion
 * happens, so the single and bulk flows can never disagree about it.
 */

export interface NameParts {
    first: string;
    middle?: string;
    last: string;
}

/**
 * Split "Chidi Emeka Okafor" into its parts.
 *
 * Returns null when there is no surname to take. A surname is required by the
 * API, so a one-word entry is a validation failure rather than something to
 * guess at — inventing an empty last name would fail server-side anyway, and
 * later in the flow where it is far more annoying.
 *
 * Everything between the first and last token becomes the middle name, so
 * "Ngozi Adaeze Chinwe Okafor" keeps "Adaeze Chinwe" rather than dropping it.
 */
export function splitFullName(full: string): NameParts | null {
    const tokens = (full ?? '').trim().split(/\s+/).filter(Boolean);
    if (tokens.length < 2) return null;

    // Guarded by the length check above, but written so noUncheckedIndexedAccess
    // sees it too.
    const first = tokens[0] ?? '';
    const last = tokens[tokens.length - 1] ?? '';
    const middle = tokens.slice(1, -1).join(' ');

    return middle ? { first, middle, last } : { first, last };
}

/** Rebuild a display name from stored parts, for prefilling a resumed draft. */
export function joinFullName(parts: {
    employeeFirstName?: string;
    employeeMiddleName?: string;
    employeeLastName?: string;
}): string {
    return [parts.employeeFirstName, parts.employeeMiddleName, parts.employeeLastName]
        .map((p) => (p ?? '').trim())
        .filter(Boolean)
        .join(' ');
}
